<template>
  <fullscreen-dialog
    v-model="model"
    :title="documentTypes[editedItem.document_type] + ' #' + editedItem?.id"
    icon="mdi-truck-check"
    color="primary"
    :save-text="$t('add_warehouse')"
    :loading="loading"
    @save="save"
  >
    <template v-slot:app-bar-actions>
      <print-button @click="print" />
    </template>

    <v-row style="border-bottom: 1px solid rgba(0, 0, 0, 0.12)" align="start">
      <v-col style="border-right: 1px solid rgba(0, 0, 0, 0.12)">
        <p class="title">
          {{ documentTypes[editedItem.document_type] }} #{{ editedItem?.id }}
        </p>
        <v-form ref="form" v-model="valid">
          <autocomplete
            v-model="editedItem.ordered_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :loading="loadingUsers"
            :label="$t('warehouse.orderer')"
            required
            disabled
            :hide-details="false"
            :rules="[rules.required]"
            color="white"
          />
          <choose-warehouse-field
            :hide-details="false"
            v-model="editedItem.warehouse_id"
            color="white"
          />
          <date-picker
            v-model="editedItem.document_date"
            :label="$t('warehouse.order_date')"
            single-date
            :hide-details="false"
            color="white"
          />
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            :hide-details="false"
            disabled
            color="white"
          />
          <text-area
            :label="$t('note')"
            :hide-details="false"
            v-model="editedItem.notes"
            rows="1"
            color="white"
          />
        </v-form>
      </v-col>
      <v-col style="border-right: 1px solid rgba(0, 0, 0, 0.12)">
        <p class="title">{{ $t("warehouse.supplier_dispatch_note") }}</p>
        <choose-supplier-field
          disabled
          :hide-details="false"
          v-model="editedItem.supplier_id"
          color="white"
        />
        <text-area
          :label="$t('note')"
          :hide-details="false"
          v-model="note"
          rows="3"
          color="white"
        />
      </v-col>
      <v-col cols="12" lg="7">
        <v-row justify="space-between" class="mb-2">
          <v-col cols="auto">
            <p class="title mb-0">
              {{ $t("warehouse.warehouse_replenishment") }}
            </p>
          </v-col>
          <v-col cols="auto" class="d-flex align-center">
            <text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              :placeholder="$t('search')"
              single-line
              hide-details
              dense
              style="max-width: 200px"
              class="mr-4"
            />
            <v-checkbox
              v-if="hasPendingGroceries"
              :value="allDelivered"
              @change="onAllDeliveredChange"
              :label="$t('warehouse.all_ordered_delivered')"
              hide-details
              dense
              class="mt-0 pt-0"
            />
          </v-col>
        </v-row>
        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <date-picker
              v-model="deliveryDate"
              :label="$t('warehouse.delivery_date')"
              single-date
              :hide-details="false"
              color="white"
            />
          </v-col>
          <v-col cols="12" md="4">
            <text-field
              :label="$t('warehouse.supplier_invoice_number')"
              v-model="dispatcherNumber"
              :hide-details="false"
              color="white"
            />
          </v-col>
          <v-col cols="12" md="4">
            <autocomplete
              v-model="editedItem.received_by"
              :items="users"
              :hide-details="false"
              return-object
              item-text="fullName"
              :label="$t('give_in_goods')"
              required
              color="white"
            />
          </v-col>
        </v-row>

        <div class="ordera-box-shadow mt-2">
          <div class="px-4">
            <v-row>
              <v-col cols="1">
                <span class="caption font-weight-bold">
                  {{ $t("r_b") }}
                </span>
              </v-col>
              <v-col cols="2">
                <span class="caption font-weight-bold">{{ $t("code") }}</span>
              </v-col>
              <v-col cols="3">
                <span class="caption font-weight-bold">{{ $t("title") }}</span>
              </v-col>
              <v-col cols="3">
                <span class="caption font-weight-bold">{{
                  $t("warehouse.ordered")
                }}</span>
              </v-col>
              <v-col cols="3" class="text-right">
                <span class="caption font-weight-bold">
                  {{ $t("warehouse.delivered") }}
                </span>
              </v-col>
            </v-row>
          </div>

          <div
            class="px-4 py-2"
            :class="i % 2 ? 'white' : 'grey lighten-3'"
            v-for="(grocery, i) in filteredGroceries"
          >
            <v-row>
              <v-col cols="1">
                <span class="body-2">{{ grocery.id }}</span>
              </v-col>
              <v-col cols="2">
                <span>{{ grocery.code }}</span>
              </v-col>
              <v-col cols="3">
                <span class="subtitle-2 font-weight-medium">{{ grocery.name }}</span>
              </v-col>
              <v-col cols="3">
                <span class="subtitle-2 font-weight-medium">
                  {{ grocery.amount }} {{ grocery.measure }}
                </span>
              </v-col>
              <v-col cols="3" class="text-right">
                <span class="subtitle-2 font-weight-medium">
                  {{ getGroceryDelivered(grocery) }} {{ grocery.measure }}
                </span>
                <v-icon
                  v-if="getGroceryDelivered(grocery) >= grocery.amount"
                  color="success"
                  small
                  class="ml-1"
                  title="Fully delivered"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
            </v-row>
            <v-row
              align="center"
              justify="end"
              v-for="transaction in getGroceryTransactions(grocery)"
            >
              <v-col cols="3">
                <text-field
                  hide-details
                  v-model="transaction.amount"
                  :color="!(i % 2) ? 'white' : ''"
                  dense
                  readonly
                  :suffix="grocery.measure"
                  type="number"
                />
              </v-col>
              <v-col lg="3" xl="2" class="text-right">
                <span>
                  {{
                    localeDate(transaction.created_at, {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  }}
                </span>
              </v-col>
            </v-row>
            <v-row
              align="center"
              justify="end"
              v-if="getGroceryDelivered(grocery) < grocery.amount"
            >
              <v-col cols="3">
                <!-- :max="grocery.amount" -->
                <text-field
                  hide-details
                  v-model="grocery.order"
                  :min="0"
                  :color="!(i % 2) ? 'white' : ''"
                  dense
                  :suffix="grocery.measure"
                  type="number"
                />
              </v-col>
              <v-col lg="3" xl="2" class="text-right">
                <span>{{ $t("warehouse.delivered_now") }}</span>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-col>
    </v-row>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasValidation from "@/mixins/hasValidation";
import hasWarehouseUsers from "@/mixins/warehouse/hasWarehouseUsers";
import ChooseWarehouseField from "@/components/warehouse/fields/ChooseWarehouseField.vue";
import ChooseSupplierField from "@/components/warehouse/fields/ChooseSupplierField.vue";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import { orderDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehousePurchaseReceiptDialog",
  mixins: [hasModel, hasValidation, hasSnackbar, hasPrinting, hasWarehouseUsers],
  components: {
    ChooseWarehouseField,
    ChooseSupplierField,
    FullscreenDialog,
  },
  props: {
    editedItem: {
      type: Object,
      default: orderDocumentGrocery,
    },
  },
  data() {
    return {
      valid: false,
      loading: false,
      note: "",
      dispatcherNumber: "",
      deliveryDate: null,
      search: "",
    };
  },
  methods: {
    onAllDeliveredChange(val) {
      if (val) {
        (this.editedItem.groceries || []).forEach((grocery) => {
          const remaining = grocery.amount - this.getGroceryDelivered(grocery);
          grocery.order = remaining > 0 ? remaining : 0;
        });
      } else {
        (this.editedItem.groceries || []).forEach((grocery) => {
          grocery.order = null;
        });
      }
    },
    checkQuantity(item) {
      if (Number(item.order) > item.amount || Number(item.order) < 0) {
        item.order = item.amount;
      }
    },
    async save() {
      this.loading = true;
      let groceries = [];

      this.editedItem.groceries.forEach((item) => {
        groceries.push({
          grocery_id: item.id,
          warehouse_id: this.editedItem.warehouse_id,
          amount: Number(item.order),
          measure: item.measure,
          transaction_type: "entry",
          notes: this.note,
          code: item.code,
        });
      });

      const params = {
        ...this.editedItem,
        document_type: "entry",
        parent_id: this.editedItem.id,
        warehouse_id: this.editedItem.warehouse_id,
        received_by: this.editedItem.received_by?.id,
        groceries,
      };
      const request = warehouseService.addDocumentTransaction(params);
      try {
        await this.handleRequest(request, {
          successMessage: this.$t("warehouse_entry_created"),
          errorMessagePrefix: this.$t("error_creating_warehouse_entry") + ": ",
        });
        const isTotallyDelivered = groceries.every(
          (grocery) => Number(grocery.order) >= grocery.amount
        );
        const isAnythingDelivered = groceries.some(
          (grocery) => Number(grocery.order) > 0
        );
        let status = "delivered-half";
        if (isTotallyDelivered) {
          status = "delivered";
        } else if (!isAnythingDelivered) {
          status = null;
        }
        if (status) {
          try {
            await warehouseService.patchWarehouse(this.editedItem.id, {
              data: { status },
            });
            this.showSuccessSnackbar(this.$t("warehouse_status_updated"));
          } catch (error) {
            this.showErrorSnackbar(this.$t("error_updating_warehouse_status"));
          }
        }
        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Failed to save invoice", error);
      } finally {
        this.loading = false;
      }
    },
    print() {
      const deliveredDate =
        this.$t("warehouse.shipping_date") + ":" + this.localeDate(new Date());
      const orderDate =
        this.$t("warehouse.ordering_date") +
        ":" +
        this.localeDate(this.editedItem.document_date);
      const orderNumber =
        this.$t("warehouse.order_number") + ": " + this.editedItem.number;
      const deliveredNumber =
        this.$t("warehouse.invoice_number") + ": " + this.dispatcherNumber;
      const note = this.note
        ? `
            <div style="font-size: 12px;margin-top: 20px;">
            ${this.$t("note")}: ${this.note}:
            </div>
          `
        : "";

      this.printDocument({
        title: this.$t("warehouse.entry_sheet") + ` #${this.editedItem.number}`,
        hideDatetime: true,
        beforeTitleHtml: `
            <div style="display: flex;justify-content: space-between;font-size: 16px;margin-top: 20px;">
              <div>${orderNumber}</div>
              <div>${deliveredNumber}</div>
            </div>
            <div style="display: flex;justify-content: space-between;font-size: 16px;">
              <div>${orderDate}</div>
              <div>${deliveredDate}</div>
            </div>
          `,
        company1: {
          id: this.editedItem.customer?.id || 101,
          name: this.toLatin(
            [this.editedItem.customer?.name, this.editedItem.customer?.lastname].join(
              " "
            ) || ""
          ),
          city: this.toLatin(this.editedItem.customer?.city || ""),
          address: this.toLatin(this.editedItem.customer?.address || ""),
          phone: this.translate(this.editedItem.customer?.phone || ""),
          email: this.translate(this.editedItem.customer?.email || ""),
          printTitle: this.$t("warehouse.orderer"),
        },
        company2: {
          ...this.editedItem.supplier,
          printTitle: this.$t("supplier"),
        },
        signature: {
          left: {
            title: this.$t("warehouse.product_delivered_by"),
            name: this.toLatin(this.editedItem.dispatched_by?.fullName || ""),
          },
          right: {
            title: this.$t("warehouse.received_by"),
            name: this.toLatin(this.editedItem.received_by?.fullName || ""),
          },
        },
        tables: [
          {
            headers: [
              ...this.headers,
              { text: this.$t("unit_of_measurment"), value: "measure" },
              { text: this.$t("price"), value: "price", align: "right" },
              { text: this.$t("amount"), value: "amount", align: "right" },
            ],
            items: this.editedItem.groceries.map((item, index) => ({
              ...item,
              num: index + 1,
              name: this.toLatin(item.name),
              price: "",
              amount: "",
            })),
          },
        ],
        afterTableHtml: note,
      });
    },
    getGroceryDelivered(grocery) {
      const transactions = this.getGroceryTransactions(grocery);
      return transactions.reduce((total, t) => total + Number(t.amount), 0);
    },
    getGroceryTransactions(grocery) {
      return this.documentTransactions.filter((t) => t.grocery_id === grocery.id);
    },
  },
  computed: {
    hasPendingGroceries() {
      return (this.editedItem.groceries || []).some(
        (grocery) => grocery.amount - this.getGroceryDelivered(grocery) > 0
      );
    },
    allDelivered() {
      const groceries = this.editedItem.groceries || [];
      const pending = groceries.filter(
        (grocery) => grocery.amount - this.getGroceryDelivered(grocery) > 0
      );
      if (!pending.length) return false;
      return pending.every((grocery) => {
        const remaining = grocery.amount - this.getGroceryDelivered(grocery);
        return Number(grocery.order) === remaining;
      });
    },
    filteredGroceries() {
      if (!this.search) {
        return this.editedItem.groceries;
      }
      const searchLower = this.translate(this.search.toLowerCase());
      return this.editedItem.groceries.filter(
        (item) =>
          this.translate(item.name.toLowerCase()).includes(searchLower) ||
          item.code.includes(searchLower)
      );
    },
    documentTransactions() {
      let transactions = [];
      (this.editedItem.children || []).forEach((child) => {
        const entryTransactions = (child.transactions || []).filter(
          (t) => t.transaction_type === "entry"
        );
        transactions = transactions.concat(entryTransactions);
      });
      return transactions;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num" },
        {
          text: this.$t("code"),
          align: "start",
          value: "code",
        },
        {
          text: this.$t("title"),
          align: "start",
          value: "name",
        },
        {
          text: this.$t("warehouse.ordered"),
          align: "start",
          value: "amount",
        },
        {
          text: this.$t("warehouse.delivered"),
          align: "start",
          value: "order",
          sortable: false,
        },
      ];
    },
    documentTypes() {
      return {
        order: this.$t("warehouse.purchase_order"),
        request: this.$t("warehouse.request_quote"),
        entry: this.$t("warehouse.entry"),
      };
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.fetchUsers();
      }
    },
  },
};
</script>
